import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { INotificationClickDTO } from 'common_library';
import { HttpIoService } from '../communication/http-io.service';

/**
 * =====================================================================
 *  DEEP LINK — punto unico di ingresso dell'app da link/notifiche.
 * =====================================================================
 *
 * COME L'APP VIENE RIAPERTA DA UN LINK
 * ------------------------------------
 * NON usiamo lo schema custom `yourfill://` (su Android non è nemmeno
 * registrato). Usiamo **Universal Links (iOS) / App Links (Android)** sul
 * dominio **https://app.yourfill.it**, già configurati in:
 *   - iOS:     ios/App/App/App.entitlements  →  applinks:app.yourfill.it
 *              src/.well-known/apple-app-site-association  →  "paths": ["*"]
 *   - Android: AndroidManifest.xml  →  <intent-filter autoVerify> host=app.yourfill.it
 *              src/.well-known/assetlinks.json
 *
 * Quindi QUALSIASI url `https://app.yourfill.it/...?<param>`:
 *   - app installata  → l'OS apre l'app, arriva qui via `handleUrl` (native)
 *                       oppure `handleParams` (web/PWA, query param);
 *   - app NON installata → apre la PWA, stesso `handleParams`.
 * Le push native arrivano via `handlePushData` (FCM data payload).
 *
 * PARAMETRI RICONOSCIUTI (tutti opzionali, possono coesistere):
 *   action_uid       → esegue un'action (registrazione, reset password…)
 *   notification_uid | uid → marca letta + apre una notifica personale
 *   open_app_info    → apre un contenuto/chapter CMS
 *   installation_id  → apre il dettaglio di una installazione
 *   open=notifications → apre la lista notifiche
 *   ct               → click token campagna (anonimo): registrato e basta
 *   hosted_hold      → rientro dalla pagina di pagamento del PSP: riferimento
 *                      opaco dell'hold pre-auth da verificare (l'app può aver
 *                      perso il paymentId se l'OS l'ha uccisa nel frattempo)
 *
 * Questo service fa SOLO parsing + registrazione click, ed emette un
 * `DeepLinkIntent` tipizzato su `intents$`. Il ROUTING (modali, login,
 * navigazione) vive in un unico handler in app.component, sottoscritto a
 * `intents$` — così la logica non è più sparsa/duplicata.
 */

export type DeepLinkIntent =
  | { kind: 'action'; actionUid: string }
  | { kind: 'notification'; notificationUid: string }
  | { kind: 'appInfo'; chapterId: string }
  | { kind: 'installation'; installationId: string }
  | { kind: 'notificationsList' }
  | { kind: 'hostedHold'; holdRef: string }
  | { kind: 'none' };

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private readonly intent$ = new Subject<DeepLinkIntent>();
  /** app.component si sottoscrive qui e fa il routing (unico punto). */
  readonly intents$: Observable<DeepLinkIntent> = this.intent$.asObservable();

  constructor(private readonly HIO: HttpIoService) {}

  /** Native universal link: `event.url` splittato sul dominio dell'environment. */
  handleUrl(url: string, domain: string): void {
    if (!url) return;
    const appPath = url.split(domain).pop() ?? '';
    const query = appPath.includes('?') ? appPath.split('?')[1] : '';
    this.process(this.fromSearchParams(new URLSearchParams(query)));
  }

  /** Web / PWA: query param dell'ActivatedRoute. */
  handleParams(params: Params): void {
    this.process(this.fromRecord(params));
  }

  /** Tap su push: FCM data payload (Record<string, unknown>). */
  handlePushData(data: Record<string, unknown> | undefined): void {
    if (!data) return;
    this.process(this.fromRecord(data));
  }

  /** URLSearchParams → record (sorgente: universal link nativo). */
  private fromSearchParams(search: URLSearchParams): Record<string, string | undefined> {
    const record: Record<string, string | undefined> = {};
    search.forEach((value, key) => { record[key] = value; });
    return record;
  }

  /** Oggetto generico (query param web o FCM data) → record di stringhe. */
  private fromRecord(source: Record<string, unknown>): Record<string, string | undefined> {
    const record: Record<string, string | undefined> = {};
    for (const key of Object.keys(source ?? {})) {
      const v = source[key];
      record[key] = typeof v === 'string' ? v : v != null ? String(v) : undefined;
    }
    return record;
  }

  private process(params: Record<string, string | undefined>): void {
    // Click campagna (anonimo): il canale è dentro il token firmato, qui si
    // POSTa e basta. Best-effort, non blocca la navigazione.
    if (params['ct']) this.recordCampaignClick(params['ct']);

    const intent = this.toIntent(params);
    if (intent.kind !== 'none') this.intent$.next(intent);
  }

  private toIntent(p: Record<string, string | undefined>): DeepLinkIntent {
    // Prima di tutto: è un rientro da un pagamento, l'utente sta aspettando un esito.
    if (p['hosted_hold']) return { kind: 'hostedHold', holdRef: p['hosted_hold'] };
    if (p['action_uid']) return { kind: 'action', actionUid: p['action_uid'] };
    const notificationUid = p['notification_uid'] ?? p['uid'];
    if (notificationUid) return { kind: 'notification', notificationUid };
    if (p['open_app_info']) return { kind: 'appInfo', chapterId: p['open_app_info'] };
    if (p['installation_id']) return { kind: 'installation', installationId: p['installation_id'] };
    if (p['open'] === 'notifications') return { kind: 'notificationsList' };
    return { kind: 'none' };
  }

  private recordCampaignClick(ct: string): void {
    const dto: INotificationClickDTO = { ct };
    this.HIO.post<{ ok: boolean }, INotificationClickDTO>('public/notification-click', dto).catch((err) => {
      console.warn('[DEEPLINK] click tracking failed', err);
    });
  }
}
